<template>
  <!--
    Dropdown menu of congressional districts. The dropdown menu requires a state in props to filter the dropdown, as there is no good scenario for a dropdown menu of 430+ districts. The menu will appear disabled without a state prop.
  -->
  <div id="cd-select-menu">
    <div v-if="lscDotGovVersion === true" class="lsc-dot-gov-styling">
      <select 
        class="lsc-gov-form-select" 
        v-model="selected"
        @change="regularSelectChange($event)"
        :id="selectMenuId"
      >
        <option v-for="district in filteredDistricts" :key="district.value" :value="district.value">{{ district.text }}</option>
      </select>
    </div>
    <div v-else id="g-select-wrapper" :class="noBottomPadding === true ? 'no-bottom-padding' : ''">
      <div id="g-select">
        <div class="g-select-fill">
          <!-- The actual select menu -->
          <div id="main-select">
            <v-select
              v-model="selected"
              :items="filteredDistricts"
              :menu-props="{ maxHeight: '400' }"
              :placeholder="hint"
              persistent-hint
              ref="areaDropdown"
              @change="setSelectedDistrict(selected, true)"
              :id="selectMenuId"
            >
            </v-select>
          </div>
        </div>
        <!-- The button (originating from OrgSelectMenu component) is repurposed from a prior v-dialog version --\> to be converted to proper button -->
        <div class="g-select-text">
          <div class="text-center">
            <v-dialog
              v-model="dialog"
              width="400"
              scrollable
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="red lighten-1"
                  text
                  v-bind="attrs"
                  v-on="on"
                  class="grantee-select-btn"
                >
                  <span class="grantee-select-btn-outer"><span class="grantee-select-btn-inner">{{ selectedCdName || hint }}</span><span class="grantee-select-btn-inner">&#9660;</span></span>
                </v-btn>
              </template>
            </v-dialog>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script> 
export default {
  name: 'CDSelect',

  props: {
    selectMenuId: { type: String, required: false, default: 'cd-select' }, // Pass in an ID for the select menu, such as to associate a label with this select menu
    lscDotGovVersion: { type: Boolean, required: false, default: false }, //LSC.gov styling
    cdPassedAsProp: { type: String, required: false, default: null },
    stFipsProp: { type: String, required: true }, // A state FIPS to filter districts. If not provided the button will display with disabled styling.
    noBottomPadding: { type: Boolean, required: false, default: false }, // Removes the default 2 rem padding if true
    neverUpdateUrlParam: { type: Boolean, required: false, default: false } // use true to read the URL parameters on page load but not change it when the dropdown changes (which updates the route and jumps to the top of the page)
  },

  data () {
    return {
      dialog: false,
      selected: null,
      stateFips: null,
      hint: 'Select a congressional district',
      numDistrictsByState: [ //118th or 119th Congress
        { st: 'Alabama', st_fips: '01', numDistricts: 7},
        { st: 'Alaska', st_fips: '02', numDistricts: 1 },
        { st: 'Arizona', st_fips: '04', numDistricts: 9 },
        { st: 'Arkansas', st_fips: '05', numDistricts: 4 },
        { st: 'California', st_fips: '06', numDistricts: 52 },
        { st: 'Colorado', st_fips: '08', numDistricts: 8 },
        { st: 'Connecticut', st_fips: '09', numDistricts: 5 },
        { st: 'Delaware', st_fips: '10', numDistricts: 1 },
        { st: 'District of Columbia', st_fips: '11', numDistricts: 1, nonVotingType: 'Delegate District (at Large)' },
        { st: 'Florida', st_fips: '12', numDistricts: 28 },
        { st: 'Georgia', st_fips: '13', numDistricts: 14 },
        { st: 'Hawaii', st_fips: '15', numDistricts: 2 },
        { st: 'Idaho', st_fips: '16', numDistricts: 2 },
        { st: 'Illinois', st_fips: '17', numDistricts: 17 },
        { st: 'Indiana', st_fips: '18', numDistricts: 9 },
        { st: 'Iowa', st_fips: '19', numDistricts: 4 },
        { st: 'Kansas', st_fips: '20', numDistricts: 4 },
        { st: 'Kentucky', st_fips: '21', numDistricts: 6 },
        { st: 'Louisiana', st_fips: '22', numDistricts: 6 },
        { st: 'Maine', st_fips: '23', numDistricts: 2 },
        { st: 'Maryland', st_fips: '24', numDistricts: 8 },
        { st: 'Massachusetts', st_fips: '25', numDistricts: 9 },
        { st: 'Michigan', st_fips: '26', numDistricts: 13 },
        { st: 'Minnesota', st_fips: '27', numDistricts: 8 },
        { st: 'Mississipi', st_fips: '28', numDistricts: 4 },
        { st: 'Missouri', st_fips: '29', numDistricts: 8 },
        { st: 'Montana', st_fips: '30', numDistricts: 2 },
        { st: 'Nebraska', st_fips: '31', numDistricts: 3 },
        { st: 'Nevada', st_fips: '32', numDistricts: 4 },
        { st: 'New Hampshire', st_fips: '33', numDistricts: 2 },
        { st: 'New Jersey', st_fips: '34', numDistricts: 12 },
        { st: 'New Mexico', st_fips: '35', numDistricts: 3 },
        { st: 'New York', st_fips: '36', numDistricts: 26 },
        { st: 'North Carolina', st_fips: '37', numDistricts: 14 },
        { st: 'North Dakota', st_fips: '38', numDistricts: 1 },
        { st: 'Ohio', st_fips: '39', numDistricts: 15 },
        { st: 'Oklahoma', st_fips: '40', numDistricts: 5 },
        { st: 'Oregon', st_fips: '41', numDistricts: 6 },
        { st: 'Pennsylvania', st_fips: '42', numDistricts: 17 },
        { st: 'Puerto Rico', st_fips: '72', numDistricts: 1, nonVotingType: 'Resident Commissioner District (at Large)' },
        { st: 'Rhode Island', st_fips: '44', numDistricts: 2 },
        { st: 'South Carolina', st_fips: '45', numDistricts: 7 },
        { st: 'South Dakota', st_fips: '46', numDistricts: 1 },
        { st: 'Tennessee', st_fips: '47', numDistricts: 9 },
        { st: 'Texas', st_fips: '48', numDistricts: 38 },
        { st: 'Utah', st_fips: '49', numDistricts: 4 },
        { st: 'Vermont', st_fips: '50', numDistricts: 1 },
        { st: 'Virginia', st_fips: '51', numDistricts: 11 },
        { st: 'Washington', st_fips: '53', numDistricts: 10 },
        { st: 'West Virginia', st_fips: '54', numDistricts: 2 },
        { st: 'Wisconsin', st_fips: '55', numDistricts: 8 },
        { st: 'Wyoming', st_fips: '56', numDistricts: 1 }
      ]
    }
  },

  computed: {
    districts () {
      let d = []
      for (let state of this.numDistrictsByState) {
        d.push({ header: state.st })
        for (let i = 0; i < state.numDistricts; i++) {
          let label, districtNum
          if (state.nonVotingType) {
            label = state.nonVotingType
            districtNum = '98'
          } else if (state.numDistricts === 1) {
            label = 'At-large'
            districtNum = '00'
          } else {
            label = `${i+1}${this.nth(i+1)}`
            districtNum = `${String(i+1).padStart(2, '0')}`
          }
          d.push({ text: label, value: `${state.st_fips}${districtNum}`, st_fips: state.st_fips, st_name: `${state.st || ''}` })
        }
      }
      return d
    },
    filteredDistricts () {
      return this.districts.filter(x => x.st_fips === this.stFipsProp)
    },
    cdsList () {
      return this.filteredDistricts.map(x => x.value)
    },
    selectedCdName () { 
      if (this.selected !== '' && this.selected !== null) {
        let district = this.districts.filter(x => x.value === this.selected)[0]
        return ['11', '72'].indexOf(district.st_fips) > -1 ? `${district.text}` : `${district.text} district`
      } else {
        return ''
      }
    }
  },

  async mounted () {
    // Clear (previously selected) CD from query param if doesn't match the state filter
    if (this.$route.query.district && this.$route.query.district.substring(0,2) !== this.stFipsProp) {
      this.setSelectedDistrict(null, true)

    // Set selected district per URL parameter if already present
    } else if (this.$route.query.district && this.cdsList.indexOf(this.$route.query.district) > -1) {
      this.setSelectedDistrict(this.$route.query.district, false)
    }

    // If an area is passed in as the pre-selected area via the "cdPassedAsProp" prop
    if (this.cdPassedAsProp !== null && this.cdPassedAsProp.length > 0) {
      this.selected = this.cdPassedAsProp
    }
  },

  methods: {
    nth(n){ //https://stackoverflow.com/questions/13627308/add-st-nd-rd-and-th-ordinal-suffix-to-a-number
      return["st","nd","rd"][((n+90)%100-10)%10-1]||"th"
    },
    regularSelectChange (event) {
      this.setSelectedDistrict(event.target.value, true)
    },
    setSelectedDistrict (val, updateUrlParam) {
      this.selected = val
      let label = this.selectedCdName
      if (updateUrlParam === true && this.neverUpdateUrlParam === false) {
        this.$router.push({ path: this.$route.path, query: { ...this.$route.query, district: val }}).catch(()=>{})
      }
      this.$emit('cdSelected', { cd: val, label: label })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/scss/org-state-select.scss';
@import '@/scss/lsc-dot-gov.scss';
</style>